/**
 * ActionCenter (C-5) — Enterprise Action Center.
 * A cross-module "open work" inbox: every non-terminal item across Deviations,
 * CAPAs, Complaints, OOS, Recalls, Registrations, and Variations in one place,
 * with overdue / due-soon flagging. Replaces module-by-module hunting.
 * Route: /action-center
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHero } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import styles from './ActionCenter.module.css';

interface ActionItem {
  entityType: string; entityId: string; number: string; title: string | null;
  status: string; due: string | null; path: string; overdue: boolean; dueSoon: boolean;
}
interface ActionResp {
  items: ActionItem[]; total: number;
  stats: { open: number; overdue: number; due_soon: number; by_module: Record<string, number> };
}
const TYPE_LABEL: Record<string, string> = {
  DEVIATION: 'Deviation', CAPA: 'CAPA', COMPLAINT: 'Complaint', OOS: 'OOS',
  RECALL: 'Recall', REGISTRATION: 'Registration', VARIATION: 'Variation',
};
const ROUTE_PATTERN: Record<string, string> = {
  DEVIATION: '/deviations/$id', CAPA: '/capa/$id', COMPLAINT: '/complaints/$id', OOS: '/oos/$id',
  RECALL: '/recalls/$id', REGISTRATION: '/regulatory/registrations/$id', VARIATION: '/regulatory/variations/$id',
  COMMITMENT: '/regulatory/commitments/$id',
};
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}

export function ActionCenter() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | 'overdue' | 'soon'>('all');
  const [module, setModule] = useState('');
  const q = useQuery<ActionResp>({
    queryKey: ['action-center'],
    queryFn: ({ signal }) => apiFetch<ActionResp>('/api/v1/action-center', { signal }),
    staleTime: 30_000,
  });

  const all = q.data?.items ?? [];
  const stats = q.data?.stats;
  const items = all.filter((i) => {
    if (tab === 'overdue' && !i.overdue) return false;
    if (tab === 'soon' && !i.dueSoon) return false;
    if (module && i.entityType !== module) return false;
    return true;
  });

  function go(it: ActionItem) {
    const pat = ROUTE_PATTERN[it.entityType];
    if (pat) navigate({ to: pat, params: { id: it.entityId } });
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="My work"
        title="Action Center"
        subtitle="Every open quality & regulatory item across the platform — one inbox, overdue work surfaced first."
      />

      {stats && (
        <div className={styles.kpiRow}>
          <button type="button" className={`${styles.kpi} ${tab === 'all' ? styles.kpiActive : ''}`} onClick={() => setTab('all')}>
            <span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open items</span>
          </button>
          <button type="button" className={`${styles.kpi} ${tab === 'overdue' ? styles.kpiActive : ''} ${stats.overdue > 0 ? styles.kpiAlert : ''}`} onClick={() => setTab('overdue')}>
            <span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span>
          </button>
          <button type="button" className={`${styles.kpi} ${tab === 'soon' ? styles.kpiActive : ''} ${stats.due_soon > 0 ? styles.kpiWarn : ''}`} onClick={() => setTab('soon')}>
            <span className={styles.kpiNum}>{stats.due_soon}</span><span className={styles.kpiLabel}>Due ≤ 7 days</span>
          </button>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Module filter" value={module} onChange={(e) => setModule(e.target.value)} className={styles.select}>
          <option value="">All modules</option>
          {Object.keys(TYPE_LABEL).map((m) => <option key={m} value={m}>{TYPE_LABEL[m]}{stats?.by_module?.[m] ? ` (${stats.by_module[m]})` : ''}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} item{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading your work…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>Nothing here — all clear.</div>
      ) : (
        <table className={styles.table}>
          <thead><tr><th>Module</th><th>Ref</th><th>Title</th><th>Status</th><th>Due</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={`${it.entityType}:${it.entityId}`} className={styles.row} onClick={() => go(it)}>
                <td><span className={styles.typeTag}>{TYPE_LABEL[it.entityType] ?? it.entityType}</span></td>
                <td className={styles.mono}>{it.number}</td>
                <td>{it.title ?? '—'}</td>
                <td><span className={styles.statusPill}>{it.status}</span></td>
                <td>
                  {it.overdue && <span className={`${styles.duePill} ${styles.dueOverdue}`}>Overdue</span>}
                  {!it.overdue && it.dueSoon && <span className={`${styles.duePill} ${styles.dueSoon}`}>Due soon</span>}
                  <span className={styles.dueDate}>{fmtDate(it.due)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
